import { NoteIcon } from '@phosphor-icons/react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useGetAmenities } from '@/api/reservations/get-amenities'
import {
  TimeBlock,
  TimeBlockReason,
  TimeBlockSortBy,
  useGetTimeBlocks,
  useInvalidateTimeBlocks,
} from '@/api/reservations/get-time-blocks'
import { TableGroup } from '@/components/layout/table-group'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Select } from '@/components/ui/select'
import { RowAction, SortOrder } from '@/components/ui/table'
import { DEFAULT_TZ } from '@/constants/date'
import { popupFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models/helpers'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import {
  selectIsDashboardToggled,
  TOGGLE_DASHBOARD,
} from '@/reducers/Organisation/reservationsReducer'

import {
  AMENITY_SPACE_COUNTS_QUERY_LIMIT,
  AmenitySpaceCounts,
  buildAmenitySpaceTreeOptions,
  getAmenitySpacePlaceholder,
  getAmenitySpaceSearchPlaceholder,
  getDefaultRange,
  getReservationsPeriodOptions,
  splitAmenitySpaceSelection,
  withDateRangeReset,
} from '../reservations-dashboard/reservations-dashboard.constants'
import { getDayEndEpoch, getDayStartEpoch, toBuildingDay } from '../reservations-dates.utils'
import { DeleteTimeBlockConfirmationPopup } from './delete-time-block-confirmation-popup'
import { TimeBlockNotesModal } from './time-block-notes-modal'
import {
  getReasonOptions,
  getTimeBlocksTableColumns,
  TIME_BLOCKS_TABLE_ID,
  timeBlocksTableFiltersSchema,
  timeBlocksTableInitialFilters,
} from './time-blocks.constants'
import * as S from './time-blocks.styled'

export const TimeBlocksTab = () => {
  const { t } = useTranslation(['reservations'])
  const dispatch = useDispatch() as any
  const activeBuilding = useSelector(selectActiveBuilding)
  const isDashboardToggled = useSelector(selectIsDashboardToggled)
  const invalidateTimeBlocks = useInvalidateTimeBlocks()

  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  useEffect(() => {
    if (isDashboardToggled) {
      invalidateTimeBlocks()
      dispatch({ toggle: false, type: TOGGLE_DASHBOARD })
    }
  }, [isDashboardToggled])

  const searchParams = useSearchParams({
    initialState: timeBlocksTableInitialFilters,
    schema: timeBlocksTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: timeBlocksTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const fromDateFilter = tableFilters.getFilter('fromDate')
  const toDateFilter = tableFilters.getFilter('toDate')
  const amenitiesAndSpacesFilter = tableFilters.getFilter('amenitiesAndSpaces') ?? []
  const reasonFilter = tableFilters.getFilter('reason') ?? []
  const sortBy = tableFilters.getFilter('sortBy')
  const sortOrder = tableFilters.getFilter('sortOrder')
  const skip = tableFilters.getFilter('skip') ?? 0
  const limit = tableFilters.getFilter('limit') ?? timeBlocksTableInitialFilters.limit ?? 25

  const allDates = !!tableFilters.getFilter('allDates')
  const [defaultFrom, defaultTo] = useMemo(() => getDefaultRange(timezone), [timezone])
  const effectiveFromDate = allDates ? undefined : (fromDateFilter ?? defaultFrom)
  const effectiveToDate = allDates ? undefined : (toDateFilter ?? defaultTo)
  const dateRange: DateRange = [
    effectiveFromDate ? toBuildingDay(effectiveFromDate, timezone) : null,
    effectiveToDate ? toBuildingDay(effectiveToDate, timezone) : null,
  ]

  const { amenityIds, locationIds } = useMemo(
    () => splitAmenitySpaceSelection(amenitiesAndSpacesFilter),
    [amenitiesAndSpacesFilter],
  )

  const { data, isFetching } = useGetTimeBlocks({
    amenityId: amenityIds.length ? amenityIds : undefined,
    buildingId,
    dateEnd: effectiveToDate,
    dateStart: effectiveFromDate,
    limit,
    locationId: locationIds.length ? locationIds : undefined,
    reason: reasonFilter.length ? (reasonFilter as TimeBlockReason[]) : undefined,
    skip,
    sortBy: sortBy as TimeBlockSortBy | undefined,
    sortOrder,
  })

  // Note: second, unpaginated fetch (same date filter, no amenity/space filter) purely to
  // tally each option's time-block count client-side - mirrors the Reservations tab's own
  // amenitySpaceCounts pattern (see AMENITY_SPACE_COUNTS_QUERY_LIMIT for the tradeoff this
  // implies).
  const { data: countsData } = useGetTimeBlocks({
    buildingId,
    dateEnd: effectiveToDate,
    dateStart: effectiveFromDate,
    limit: AMENITY_SPACE_COUNTS_QUERY_LIMIT,
    skip: 0,
  })
  console.log('countsData', countsData)
  const amenitySpaceCounts = useMemo(() => {
    const counts: AmenitySpaceCounts = {}
    for (const block of countsData?.blocks ?? []) {
      if (!block.amenityId) continue
      const amenityKey = `a-${block.amenityId.id}`
      counts[amenityKey] = (counts[amenityKey] ?? 0) + 1
      if (block.locationId) {
        const locationKey = `l-${block.locationId.id}`
        counts[locationKey] = (counts[locationKey] ?? 0) + 1
      }
    }
    return counts
  }, [countsData])

  const { data: amenities = [] } = useGetAmenities(buildingId)
  const amenitySpaceTreeOptions = useMemo(
    () => buildAmenitySpaceTreeOptions(amenities, amenitySpaceCounts),
    [amenities, amenitySpaceCounts],
  )

  const columns = useMemo(
    () =>
      getTimeBlocksTableColumns(t, timezone, {
        onDelete: (row) =>
          popupFactory.open(DeleteTimeBlockConfirmationPopup, { timeBlockId: row.id }),
      }),
    [t, timezone],
  )

  const rowActions: RowAction<TimeBlock>[] = [
    {
      icon: NoteIcon,
      label: t('reservations:timeBlocks.actions.notes'),
      onClick: (row) =>
        popupFactory.open(TimeBlockNotesModal, {
          timeBlockId: row.id,
          timezone,
        }),
      primary: true,
    },
  ]

  const handleSort = (nextSortBy: string | null, nextSortOrder: SortOrder | null) => {
    tableFilters.setFilters({
      sortBy: nextSortBy ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  return (
    <TableGroup
      id={TIME_BLOCKS_TABLE_ID}
      columns={columns}
      data={data?.blocks ?? []}
      emptyNoFilters={{
        description: t('reservations:timeBlocks.empty.noFilters.description'),
        title: t('reservations:timeBlocks.empty.noFilters.title'),
      }}
      emptyWithFilters={{
        description: t('reservations:timeBlocks.empty.withFilters.description'),
        title: t('reservations:timeBlocks.empty.withFilters.title'),
      }}
      filters={
        <>
          <S.AmenitySpaceSelectWrapper>
            <Select
              checkbox
              searchable
              type="nested"
              multipleSelectedLabel={t(
                'reservations:shared.amenitySpaceFilter.multipleSelectedLabel',
              )}
              options={amenitySpaceTreeOptions}
              placeholder={getAmenitySpacePlaceholder(t)}
              searchPlaceholder={getAmenitySpaceSearchPlaceholder(
                t,
                amenitiesAndSpacesFilter.length,
              )}
              size="small"
              value={amenitiesAndSpacesFilter}
              onChange={(value) => tableFilters.setFilter('amenitiesAndSpaces', value)}
              onReset={() => tableFilters.setFilter('amenitiesAndSpaces', [])}
            />
          </S.AmenitySpaceSelectWrapper>
          <Select
            options={getReasonOptions(t)}
            placeholder={
              reasonFilter.length
                ? t('reservations:timeBlocks.filters.reason.multipleSelectedLabel')
                : t('reservations:timeBlocks.filters.reason.placeholder')
            }
            size="small"
            type="multi"
            value={reasonFilter}
            onChange={(value) => tableFilters.setFilter('reason', value)}
            onReset={() => tableFilters.setFilter('reason', [])}
          />
        </>
      }
      getRowKey={(row) => row.id}
      loading={isFetching}
      sortBy={sortBy ?? null}
      sortOrder={sortOrder ?? null}
      navigation={{
        datePicker: (
          <DatePickerInput
            mode="range"
            periodOptions={getReservationsPeriodOptions(t, timezone)}
            placeholder={t('reservations:shared.datePicker.placeholder')}
            size="small"
            value={dateRange}
            onRangeChange={(range) => {
              const fromDate = range[0] ? getDayStartEpoch(range[0], timezone) : undefined
              const toDate = range[1] ? getDayEndEpoch(range[1], timezone) : undefined
              tableFilters.setFilters({ allDates: !range[0] && !range[1], fromDate, toDate })
            }}
          />
        ),
      }}
      pagination={{
        paginationFilters: tableFilters,
        total: data?.count ?? 0,
      }}
      rowActions={rowActions}
      tableFilters={withDateRangeReset(tableFilters)}
      onSort={handleSort}
    />
  )
}
